import { useDispatch, useSelector } from 'react-redux';
import { Field, FormSection, WrappedFieldProps } from 'redux-form';
import {
  isSingleDelivery,
  getAdditionalServices,
  selectShouldUseNewCart,
  getSelectedDelivery
} from 'views/CheckoutPage/DeliveryInformation/selector';
import {
  getCartItems,
  getSubItemRelations,
  getBobSubItemRelations
} from 'components/Cart/selector';
import { CheckoutDeliveryItem } from 'models/CheckoutDeliveryItem';
import { NO_ELEMENT, rm } from 'utils';
import { PageState } from 'models/PageState';
import {
  selectDeliveryOption,
  toggleAdditionalService
} from 'views/CheckoutPage/DeliveryInformation/actions';
import { ICart } from 'models/ICart';
import { ServiceId } from 'views/CheckoutPage/DeliveryInformation/actions';
import { DeliveryOptionItem } from 'models/DeliveryOptionItem';
import styles from './delivery.scss';
import { DeliveryOption } from './deliveryOption';
import { ItemRelationsResponse } from 'models/ItemRelationsResponse';
import { RelationshipType } from 'models/RelationshipType';
import { GtmAddShippingInfo } from 'store/googleTagManager/actions';
import batchAction from 'store/batching';
import { withCache } from 'utils/withCache';
import { ThunkDispatch } from 'components/Shared/connect';

interface OwnProps extends CheckoutDeliveryItem {
  onServicePointSelect: (
    deliveryId: string,
    deliveryOption: DeliveryOptionItem,
    syncServicePoints: boolean
  ) => void;
  disabled?: boolean;
  hasRelatedDeliveries: boolean;
}

const CRAMO_IDENTIFIER = '1969351';

const getBobBundleItems = (bobCartRelations: ItemRelationsResponse[]) => {
  return getPackageItems(bobCartRelations).flatMap(relation => relation.value.items);
};

const getPackageItems = (bobCartRelations: ItemRelationsResponse[]) => {
  return Object.values(bobCartRelations).filter(
    relation => relation.value.type === RelationshipType.Package
  );
};

interface EmitAddShippingInfo {
  dispatch: ThunkDispatch;
}
const emitAddShippingInfo = ({ dispatch }: EmitAddShippingInfo) => {
  const addShippingInfoKey = 'added_shipping_info';
  const shouldEmitAddShippingInfo = withCache(cache => {
    const hasEmitted = cache.get(addShippingInfoKey);
    if (hasEmitted) return false;
    cache.set(addShippingInfoKey, true);
    return true;
  });

  if (!shouldEmitAddShippingInfo) return;
  dispatch(GtmAddShippingInfo());
};

export const Delivery = ({
  disabled,
  name,
  identifier,
  deliveryOptions = [], // Shouldn't be undefined but sometimes it is
  deliveryTime,
  deliveryTimeMessage,
  deliveryPlace,
  items,
  onServicePointSelect,
  hasRelatedDeliveries
}: OwnProps) => {
  const dispatch = useDispatch();
  const regularDeliveryOptions = deliveryOptions.filter(
    d => d.servicePointInformation === undefined
  );

  const selectedDeliveryId = useSelector<PageState>(state => {
    const selectedDeliveryOption = getSelectedDelivery(state, identifier);

    if (selectedDeliveryOption) {
      return selectedDeliveryOption;
    }

    return regularDeliveryOptions.length !== 0
      ? regularDeliveryOptions[0].deliveryOptionIdentifier
      : undefined;
  });

  const shouldUseNewCart = useSelector(selectShouldUseNewCart);
  const cartItems = useSelector(getCartItems);
  const relations = useSelector(getSubItemRelations);
  const bobsubItemRelations = useSelector(getBobSubItemRelations);
  const bobBundleItems = getBobBundleItems(bobsubItemRelations);

  const relationItems = shouldUseNewCart
    ? Object.values(relations)
        .flatMap(relation => relation.items)
        .concat([...bobBundleItems])
    : [];

  const _items = [...cartItems, ...relationItems];

  const singleDelivery = useSelector(isSingleDelivery);
  const selectedAdditionalServices = useSelector<PageState, string[]>(state =>
    getAdditionalServices(state, identifier)
  );

  const cartItemsInDelivery = _items.filter(cartItem => items.includes(cartItem.cartItemId));

  const servicePointDeliveryOptions = deliveryOptions.filter(d => d.servicePointInformation);
  const showRegularOptions = regularDeliveryOptions.length > 0;
  const hasDeliveryOptionsToDisplay = showRegularOptions || servicePointDeliveryOptions.length > 0;

  const deliveryMessage = deliveryTimeMessage || shouldUseNewCart ? deliveryTime : '';
  const handleToggleAdditionalService = (
    serviceId: ServiceId,
    deliveryId: string,
    optionId: string
  ) => dispatch(toggleAdditionalService(serviceId, deliveryId, optionId));
  const resetAdditionalServices = () => {
    deliveryOptions.map(o => {
      o.additionalServices &&
        o.additionalServices.map(as => {
          if (selectedAdditionalServices.includes(as.id)) {
            handleToggleAdditionalService(
              as.id,
              o.identifier.identifier,
              o.deliveryOptionIdentifier
            );
          }
        });
    });
  };

  if (!disabled) {
    emitAddShippingInfo({ dispatch });
  }

  const handleSelectDeliveryOption = (deliveryOptionId: string) => {
    resetAdditionalServices();
    dispatch(batchAction(selectDeliveryOption(identifier, deliveryOptionId)));
  };

  if (cartItemsInDelivery.length === 0) {
    return NO_ELEMENT;
  }

  return (
    <>
      <FormSection name={`deliveries.delivery-${identifier}`}>
        <div className={styles.delivery}>
          <div className={styles.titleRow}>
            <h3 className={styles.title}>
              {singleDelivery ? rm('delivery-single-delivery-title') : name}
            </h3>
            {Boolean(deliveryMessage) && (
              <span className={styles.deliveryTime}>
                {identifier === CRAMO_IDENTIFIER
                  ? rm('checkout_delivery_cramo_pickup')
                  : rm('delivery_message_rd3', deliveryPlace, deliveryMessage)}
              </span>
            )}
          </div>

          {shouldUseNewCart ? (
            <NewCartItem cartItems={cartItemsInDelivery} />
          ) : (
            <CartItem cartItems={cartItemsInDelivery} subItemRelations={relations} />
          )}

          {hasDeliveryOptionsToDisplay && (
            <Field
              component={RenderDeliveryOptions}
              name={identifier}
              required
              disabled={disabled}
              deliveryOptions={deliveryOptions}
              selectedDeliveryId={selectedDeliveryId}
              onSelect={handleSelectDeliveryOption}
              identifier={identifier}
              onServicePointSelect={onServicePointSelect}
              hasRelatedDeliveries={hasRelatedDeliveries}
            />
          )}
        </div>
      </FormSection>
    </>
  );
};

type CartItemProps = Pick<ICart, 'cartItems' | 'subItemRelations'>;
const CartItem = ({ cartItems, subItemRelations }: CartItemProps) => {
  return (
    <div className={styles.cartItems}>
      {cartItems.map(({ cartItemId, displayName }) => (
        <div key={cartItemId} className={styles.cartItem}>
          <span className={styles.displayName}>{displayName}</span>
          {subItemRelations[cartItemId]?.items && (
            <div className={styles.relatedproducts}>
              {subItemRelations[cartItemId]?.items?.map(subItem => (
                <div key={subItem.cartItemId}>
                  <span>+ {subItem.displayName}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

type NewCartItemProps = Pick<ICart, 'cartItems'>;
const NewCartItem = ({ cartItems }: NewCartItemProps) => {
  return (
    <div className={styles.cartItems}>
      {cartItems.map(({ cartItemId, displayName }) => (
        <div key={cartItemId} className={styles.cartItem}>
          <span className={styles.displayName}>{displayName}</span>
        </div>
      ))}
    </div>
  );
};

interface RenderDeliveryOptionsProps extends WrappedFieldProps {
  disabled: boolean;
  deliveryOptions: DeliveryOptionItem[];
  selectedDeliveryId: string;
  onSelect: (deliveryOptionId: string) => void;
  identifier: string;
  onServicePointSelect?: (
    deliveryId: string,
    deliveryOption: DeliveryOptionItem,
    syncServicePoints: boolean
  ) => void;
  hasRelatedDeliveries: boolean;
}

const RenderDeliveryOptions = ({
  deliveryOptions,
  selectedDeliveryId,
  onSelect,
  input,
  identifier,
  onServicePointSelect,
  hasRelatedDeliveries
}: RenderDeliveryOptionsProps) => {
  const servicePointDeliveryOptions = deliveryOptions.filter(d => d.servicePointInformation);
  const regularDeliveryOptions = deliveryOptions.filter(
    d => d.servicePointInformation === undefined && d.name !== ''
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onChange = (value: any) => {
    onSelect(value);
  };
  if (regularDeliveryOptions.length <= 0 && servicePointDeliveryOptions.length <= 0) {
    return NO_ELEMENT;
  }
  return (
    <div className={styles.deliveryOptionsContainer}>
      <ul className={styles.deliveryOptions}>
        {regularDeliveryOptions.map(o => {
          return (
            <li key={o.deliveryOptionIdentifier} className={styles.deliveryOption}>
              <DeliveryOption
                deliveryOptions={[o]}
                selectedDeliveryId={selectedDeliveryId}
                input={{ ...input, onChange }}
                identifier={identifier}
                hasRelatedDeliveries={false}
              />
            </li>
          );
        })}
        {servicePointDeliveryOptions.length > 0 && (
          <li
            key={servicePointDeliveryOptions[0].deliveryOptionIdentifier}
            className={styles.deliveryOption}
          >
            <DeliveryOption
              deliveryOptions={servicePointDeliveryOptions}
              selectedDeliveryId={selectedDeliveryId}
              input={{ ...input, onChange }}
              identifier={identifier}
              onServicePointSelect={onServicePointSelect}
              hasRelatedDeliveries={hasRelatedDeliveries}
              displayOpeningHours={identifier === CRAMO_IDENTIFIER}
            />
          </li>
        )}
      </ul>
    </div>
  );
};
